import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteAlarmRule, patchAlarmRuleEnabled, AttributeTypeLite } from '@/api/alertsApi';

export type AlarmRuleScope = 'box' | 'device' | 'attribute' | 'attribute_type';

export interface UiAlarmRuleTarget {
  kind: AlarmRuleScope;
  value: unknown;
}

export interface UiAlarmRule {
  id: number;
  enabled: boolean;
  typeLabel: string;
  scope: AlarmRuleScope;
  target: UiAlarmRuleTarget | null;
  operator: string | null;
  thresholdValue: string | null;
  offlineAfterSeconds: number | null;
  cooldownSeconds: number | null;
  updatedAt: string;
}

function iriLast(iri: string): string {
  const last = iri.split('/').pop();
  return last || iri;
}

function labelScope(kind: AlarmRuleScope): string {
  switch (kind) {
    case 'box':
      return 'Box';
    case 'device':
      return 'Équipement';
    case 'attribute':
      return 'Attribut';
    case 'attribute_type':
      return 'Type d’attribut';
    default:
      return kind;
  }
}

function formatTarget(target: UiAlarmRuleTarget | null, attributeTypes: AttributeTypeLite[] = []): string {
  if (!target) return '—';

  const v = target.value as any;
  if (typeof v === 'string') {
    // Try to resolve attribute type name from IRI
    if (target.kind === 'attribute_type') {
      const typeId = iriLast(v);
      const foundType = attributeTypes.find((t) => String(t.id) === typeId);
      if (foundType) {
        const name = foundType.name || foundType.code || `#${foundType.id}`;
        return `${labelScope(target.kind)}: ${name}`;
      }
    }
    return `${labelScope(target.kind)}: #${iriLast(v)}`;
  }
  if (v && typeof v === 'object') {
    const name = v.name || v.code || v.label;
    const id = v.id ? `#${v.id}` : '';
    return `${labelScope(target.kind)}: ${name ? String(name) : id || '[objet]'}`;
  }
  return `${labelScope(target.kind)}: —`;
}

function formatParams(rule: UiAlarmRule): string {
  const parts: string[] = [];

  if (rule.offlineAfterSeconds !== null && rule.offlineAfterSeconds !== undefined) {
    parts.push(`Hors ligne après ${rule.offlineAfterSeconds}s`);
  }
  if (rule.operator && (rule.thresholdValue !== null && rule.thresholdValue !== undefined)) {
    parts.push(`Seuil ${rule.operator} ${rule.thresholdValue}`);
  }
  if (rule.cooldownSeconds !== null && rule.cooldownSeconds !== undefined) {
    parts.push(`Cooldown ${rule.cooldownSeconds}s`);
  }

  return parts.length ? parts.join(' • ') : '—';
}

function formatDate(value: string): string {
  try {
    const d = new Date(value);
    return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return value;
  }
}

interface AlarmRulesTableProps {
  rules: UiAlarmRule[];
  isLoading: boolean;
  error: unknown;
  attributeTypes?: AttributeTypeLite[];
}

export function AlarmRulesTable({ rules, isLoading, error, attributeTypes = [] }: AlarmRulesTableProps) {
  const rows = useMemo(() => rules, [rules]);
  const queryClient = useQueryClient();

  const toggleEnabledMutation = useMutation({
    mutationFn: async (vars: { id: number; enabled: boolean }) => {
      return await patchAlarmRuleEnabled(vars.id, vars.enabled);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alarmRules'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await deleteAlarmRule(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alarmRules'] });
    },
  });

  if (isLoading) {
    return <div className="p-6 text-sm text-slate-500 dark:text-slate-400">Chargement…</div>;
  }

  if (error) {
    return (
      <div className="p-6 text-sm text-red-500">
        Erreur lors du chargement des règles.
      </div>
    );
  }

  if (!rows.length) {
    return <div className="p-6 text-sm text-slate-500 dark:text-slate-400">Aucune règle.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50 dark:bg-[#1E293B]">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Activée
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Scope
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Cible
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Paramètres
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Mis à jour
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-[#1E293B]">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-[#1E293B]">
              <td className="px-6 py-4 whitespace-nowrap">
                <button
                  type="button"
                  onClick={() => {
                    if (toggleEnabledMutation.isPending || deleteMutation.isPending) return;
                    toggleEnabledMutation.mutate({ id: r.id, enabled: !r.enabled });
                  }}
                  className={`inline-flex items-center gap-2 text-sm transition-colors ${
                    r.enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'
                  } ${toggleEnabledMutation.isPending ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-80'}`}
                  title="Basculer activée"
                >
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      r.enabled ? 'bg-emerald-500' : 'bg-slate-400'
                    }`}
                  />
                  {r.enabled ? 'Oui' : 'Non'}
                </button>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 dark:text-slate-100">
                {r.typeLabel}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 dark:text-slate-200">
                {labelScope(r.scope)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 dark:text-slate-200">
                {formatTarget(r.target, attributeTypes)}
              </td>
              <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-200">
                {formatParams(r)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                {formatDate(r.updatedAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <button
                  type="button"
                  onClick={() => {
                    if (toggleEnabledMutation.isPending || deleteMutation.isPending) return;
                    const ok = window.confirm('Supprimer cette règle ?');
                    if (!ok) return;
                    deleteMutation.mutate(r.id);
                  }}
                  className="inline-flex items-center justify-center p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-slate-100 dark:hover:bg-[#0B1116] transition-colors"
                  title="Supprimer"
                >
                  <span className="material-icons-outlined" style={{ fontSize: '20px' }}>
                    delete
                  </span>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

